import React, { useState, useRef} from 'react';
import axios from 'axios';
import { Stage, Layer, Image as KonvaImage, Rect, Text, Group, Line, Circle } from 'react-konva';
import useImage from 'use-image';
import { jsPDF } from "jspdf";
import SignatureCanvas from 'react-signature-canvas';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, FileText, Download, ZoomIn, ZoomOut, 
  Undo, Redo, X, ChevronLeft, ChevronRight, PenTool, 
  Database, LayoutTemplate, Sparkles, Moon, Sun, Minus, Plus, Bot
} from 'lucide-react';

// --- THEME DEFINITIONS ---
const THEMES = {
  light: {
    id: 'light',
    bg: "#F8FAFC",      
    surface: "#FFFFFF",
    text: "#0F172A",    
    textSecondary: "#64748B", 
    border: "#E2E8F0",  
    primary: "#2563EB", 
    primaryHover: "#1D4ED8",
    danger: "#EF4444",
    success: "#10B981",
    grid: "#CBD5E1", 
    hover: "#F1F5F9"
  },
  dark: {
    id: 'dark',
    bg: "#0F172A",      
    surface: "#1E293B", 
    text: "#F8FAFC",    
    textSecondary: "#94A3B8", 
    border: "#334155",  
    primary: "#3B82F6", 
    danger: "#F87171",
    success: "#34D399",
    grid: "#334155",
    hover: "#334155"
  }
};

const URLImage = ({ src, x, y, width, height }) => {
  const [image] = useImage(src);
  return image ? <KonvaImage image={image} x={x} y={y} width={width} height={height} /> : null;
};

function App() {
  // --- STATE ---
  const [isDarkMode, setIsDarkMode] = useState(false);
  const theme = isDarkMode ? THEMES.dark : THEMES.light;

  const [mode, setMode] = useState('simple'); 
  const [pages, setPages] = useState([]); 
  const [currentPage, setCurrentPage] = useState(0);
  const [scale, setScale] = useState(1.0); 
  const [fontSize, setFontSize] = useState(14); 
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // AI State
  const [showMagicPaste, setShowMagicPaste] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);
  const [selectedBoxId, setSelectedBoxId] = useState(null);
  const [showSigModal, setShowSigModal] = useState(false);
  const [isSidebarOpen] = useState(true);

  const sigPadRef = useRef({});
  const stageRef = useRef(null);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null); 

  const currentPageData = pages[currentPage];
  const imageData = currentPageData ? currentPageData.image : null;
  const currentBoxes = currentPageData ? currentPageData.boxes : [];
  
  const originalWidth = currentPageData ? currentPageData.width : 800;
  const originalHeight = currentPageData ? currentPageData.height : 1000;

  // --- LOGIC ---
  const saveHistory = (newPages) => {
    const boxesSnapshot = newPages.map(p => p.boxes);
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push(boxesSnapshot);
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);
  };

  const updatePages = (newPages) => {
    setPages(newPages);
    saveHistory(newPages);
  };

  const handleUndo = () => {
    if (historyStep <= 0) return;
    const prevStep = historyStep - 1;
    const prevBoxes = history[prevStep];
    const newPages = pages.map((p, i) => ({ ...p, boxes: prevBoxes[i] }));
    setPages(newPages);
    setHistoryStep(prevStep);
  };

  const handleRedo = () => {
    if (historyStep >= history.length - 1) return;
    const nextStep = historyStep + 1;
    const nextBoxes = history[nextStep];
    const newPages = pages.map((p, i) => ({ ...p, boxes: nextBoxes[i] }));
    setPages(newPages);
    setHistoryStep(nextStep);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    
    const formData = new FormData();
    formData.append("file", file);
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

    try {
      const res = await axios.post(`${API_URL.replace(/\/$/, "")}/detect`, formData);
      const newPages = res.data.pages.map(page => ({
          ...page,
          boxes: page.boxes.map(b => ({
              ...b,
              userText: "",
              type: b.label === "choice_button" ? "checkbox" : "text",
              signatureUrl: null
          }))
      }));

      setPages(newPages);
      setCurrentPage(0);
      setHistory([newPages.map(p => p.boxes)]);
      setHistoryStep(0);
      
      if (newPages[0] && containerRef.current) {
          const containerWidth = containerRef.current.offsetWidth - 100;
          const fitScale = containerWidth / newPages[0].width;
          setScale(Math.min(1.0, Math.max(0.4, fitScale))); 
      }

    } catch (err) {
      console.error(err);
      alert("Error connecting to backend.");
    }
    setLoading(false);
  };

  const updateBoxValue = (id, key, val) => {
    const newPages = [...pages];
    newPages[currentPage].boxes = newPages[currentPage].boxes.map(b => 
       b.id === id ? { ...b, [key]: val } : b
    );
    updatePages(newPages);
  };

  const handleDelete = (e, id) => {
    if(e) e.cancelBubble = true;
    const newPages = [...pages];
    newPages[currentPage].boxes = newPages[currentPage].boxes.filter(b => b.id !== id);
    updatePages(newPages);
    setSelectedBoxId(null);
  };

  const handleExportJSON = () => {
    const schema = [];
    const data = {};
    pages.forEach((page, pIndex) => {
        page.boxes.forEach(box => {
            schema.push({
                key: box.fieldName,
                type: box.type,
                page: pIndex,
                rect: [box.x, box.y, box.width, box.height]
            });
            if (box.userText || box.signatureUrl) {
                data[box.fieldName] = box.userText;
            }
        });
    });
    const blob = new Blob([JSON.stringify({ schema, data }, null, 2)], { type: "application/json" });
    saveAs(blob, "form_template.json");
  };

  const handleLoadJSON = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target.result);
        const dataObj = json.data || json; 
        const newPages = pages.map(page => ({
            ...page,
            boxes: page.boxes.map(box => {
                if (dataObj[box.fieldName] !== undefined) {
                    return { ...box, userText: dataObj[box.fieldName] };
                }
                return box;
            })
        }));
        updatePages(newPages);
      } catch (err) { alert("Invalid JSON"); }
    };
    reader.readAsText(file);
  };

  const handleBulkCSV = async (e) => {
    const file = e.target.files[0];
    if(!file) return;
    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
        const text = event.target.result;
        const rows = text.trim().split('\n').map(row => row.split(','));
        const headers = rows[0].map(h => h.trim());
        const dataRows = rows.slice(1);
        const zip = new JSZip();

        for (let i = 0; i < dataRows.length; i++) {
            const rowData = dataRows[i];
            const tempPages = pages.map(page => ({
                ...page,
                boxes: page.boxes.map(box => {
                    const headerIndex = headers.indexOf(box.fieldName);
                    if (headerIndex !== -1 && rowData[headerIndex]) {
                         const val = rowData[headerIndex].trim();
                         if (box.type === "checkbox") {
                             const isChecked = (val.toLowerCase() === "x" || val.toLowerCase() === "true");
                             return { ...box, userText: isChecked ? "X" : "" };
                         }
                         return { ...box, userText: val };
                    }
                    return box;
                })
            }));
            const pdfBlob = await generatePDFBlob(tempPages);
            zip.file(`record_${i+1}.pdf`, pdfBlob);
        }
        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, "bulk_forms.zip");
        setLoading(false);
    };
    reader.readAsText(file);
  };

  // --- NEW: AI MAGIC FILL HANDLER ---
  const handleMagicFill = async () => {
    if (!pasteText) return;
    setLoading(true);
    setShowMagicPaste(false); 

    // Flatten all boxes from all pages to send to AI
    const allBoxes = [];
    pages.forEach(page => {
        page.boxes.forEach(box => {
            allBoxes.push({
                id: box.id,
                label: box.label || "text",
                fieldName: box.fieldName
            });
        });
    });

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

    try {
      const res = await axios.post(`${API_URL.replace(/\/$/, "")}/auto-fill`, {
        text: pasteText,
        fields: allBoxes
      });

      const aiData = res.data.data; // { "p0_1": "John Doe", "p0_2": "Engineer" }

      if (!aiData || Object.keys(aiData).length === 0) {
          alert("AI couldn't match any fields. Try pasting more detailed text.");
      } else {
          // Update State with AI Data
          const newPages = pages.map(page => ({
            ...page,
            boxes: page.boxes.map(box => {
              if (aiData[box.id]) {
                return { ...box, userText: aiData[box.id] };
              }
              return box;
            })
          }));
          updatePages(newPages);
          alert(`✨ Success! AI filled ${Object.keys(aiData).length} fields.`);
      }

    } catch (err) {
      console.error(err);
      alert("AI Error: " + err.message);
    }
    setLoading(false);
    setPasteText("");
  };

  const generatePDFBlob = async (pagesData) => {
    const firstPage = pagesData[0];
    const pdf = new jsPDF({
        orientation: firstPage.width > firstPage.height ? 'l' : 'p',
        unit: 'px',
        format: [firstPage.width, firstPage.height]
    });

    for (let i = 0; i < pagesData.length; i++) {
        const page = pagesData[i];
        if (i > 0) pdf.addPage([page.width, page.height]);
        pdf.addImage(page.image, 'PNG', 0, 0, page.width, page.height);

        page.boxes.forEach(box => {
            if (box.type === "text") {
                pdf.setFontSize(fontSize);
                const textWidth = pdf.getTextWidth(box.userText || "");
                const xOffset = (box.width - textWidth) / 2;
                const yOffset = (box.height + fontSize/3) / 2; 
                pdf.text(box.userText || "", box.x + xOffset, box.y + yOffset);
            }
            else if (box.type === "checkbox" && box.userText === "X") {
                pdf.setLineWidth(2);
                pdf.line(box.x, box.y, box.x + box.width, box.y + box.height);
                pdf.line(box.x + box.width, box.y, box.x, box.y + box.height);
            }
            else if (box.type === "signature" && box.signatureUrl) {
                pdf.addImage(box.signatureUrl, 'PNG', box.x, box.y, box.width, box.height);
            }
        });
    }
    return pdf.output('blob');
  };

  const handleSavePDF = async () => {
    setSelectedBoxId(null);
    setIsSaving(true);
    setTimeout(async () => {
       const blob = await generatePDFBlob(pages);
       saveAs(blob, "filled_form.pdf");
       setIsSaving(false);
    }, 100);
  };

  const handleBoxClick = (box) => {
    if (isSaving) return;
    if (box.type === "checkbox") {
      const newVal = box.userText === "X" ? "" : "X";
      updateBoxValue(box.id, 'userText', newVal);
      return;
    }
    if (box.type === "signature") {
      setSelectedBoxId(box.id);
      setShowSigModal(true);
      return;
    }
    setSelectedBoxId(box.id);
  };

  const saveSignature = () => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
       const dataUrl = sigPadRef.current.getCanvas().toDataURL('image/png');
       updateBoxValue(selectedBoxId, 'signatureUrl', dataUrl);
       setShowSigModal(false);
    }
  };

  const LoadingOverlay = () => (
    <motion.div 
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{...styles.loadingOverlay, background: isDarkMode ? "rgba(15, 23, 42, 0.9)" : "rgba(255,255,255,0.9)"}}>
      <div style={{...styles.loadingSpinner, borderTopColor: theme.primary, borderColor: theme.border}}></div>
      <p style={{ marginTop: 20, color: theme.primary, fontWeight: 'bold' }}>AI Processing...</p>
    </motion.div>
  );

  return (
    <div style={{...styles.appContainer, background: theme.bg, color: theme.text}}>
      {/* HEADER */}
      <header style={{...styles.header, background: theme.surface, borderColor: theme.border}}>
        <div style={styles.logoSection}>
          <div style={{...styles.logoIcon, background: theme.primary}}><FileText size={20} color="white" /></div>
          <h3 style={styles.logoText}>Fillable<span style={{color: theme.primary}}>AI</span></h3>
        </div>

        <div style={{...styles.modeSwitch, background: theme.bg}}>
          <button onClick={() => setMode('simple')} style={mode === 'simple' ? {...styles.modeBtnActive, background: theme.surface, color: theme.primary} : {...styles.modeBtn, color: theme.textSecondary}}>
            <PenTool size={14} style={{marginRight: 6}}/> Edit
          </button>
          <button onClick={() => setMode('template')} style={mode === 'template' ? {...styles.modeBtnActive, background: theme.surface, color: theme.primary} : {...styles.modeBtn, color: theme.textSecondary}}>
            <LayoutTemplate size={14} style={{marginRight: 6}}/> Automate
          </button>
        </div>

        <div style={styles.headerActions}>
           <button onClick={() => setIsDarkMode(!isDarkMode)} style={{...styles.iconBtn, color: theme.textSecondary, marginRight: 10}}>
             {isDarkMode ? <Sun size={20}/> : <Moon size={20}/>}
           </button>
           {pages.length > 0 && (
             <button onClick={handleSavePDF} disabled={isSaving} style={{...styles.primaryBtn, background: theme.primary}}>
                <Download size={16} style={{marginRight: 8}}/> {isSaving ? "Exporting..." : "Export PDF"}
             </button>
           )}
        </div>
      </header>

      {/* MAIN LAYOUT */}
      <div style={styles.mainLayout}>
        
        {/* SIDEBAR */}
        <AnimatePresence>
          {mode === 'template' && isSidebarOpen && (
            <motion.div 
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              style={{...styles.sidebar, background: theme.surface, borderColor: theme.border}}
            >
              <div style={{...styles.sidebarHeader, borderColor: theme.border}}>
                <h4>Automation Tools</h4>
                <Database size={16} color={theme.textSecondary}/>
              </div>
              
              <div style={styles.sidebarContent}>
                
                {/* 🌟 NEW AI MAGIC PASTE SECTION 🌟 */}
                <div style={{marginBottom: "24px", padding: "16px", background: "linear-gradient(135deg, #6366F1 0%, #A855F7 100%)", borderRadius: "12px", color: "white", textAlign: "center", boxShadow: "0 4px 15px rgba(99, 102, 241, 0.3)"}}>
                    <div style={{marginBottom: "10px", fontWeight: "700", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontSize: "14px"}}>
                        <Bot size={20} /> AI Auto-Fill
                    </div>
                    <p style={{fontSize: "12px", opacity: 0.9, marginBottom: "14px", lineHeight: "1.4"}}>Paste your Resume, Bio, or LinkedIn to auto-fill this form.</p>
                    <button 
                        onClick={() => setShowMagicPaste(true)}
                        style={{background: "white", color: "#7C3AED", border: "none", padding: "8px 12px", borderRadius: "6px", fontWeight: "700", cursor: "pointer", width: "100%", fontSize: "13px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)"}}
                    >
                       ✨ Magic Paste
                    </button>
                </div>

                <div style={styles.sidebarSection}>
                   <h5 style={{...styles.sectionTitle, color: theme.textSecondary}}>1. Data Source</h5>
                   <div style={styles.buttonGroup}>
                      <label style={{...styles.secondaryBtn, borderColor: theme.border, color: theme.text, background: theme.bg}}>
                         <Upload size={14} style={{marginRight: 6}}/> Load JSON
                         <input type="file" hidden onChange={handleLoadJSON} accept=".json" />
                      </label>
                      <button style={{...styles.secondaryBtn, borderColor: theme.border, color: theme.text, background: theme.bg}} onClick={handleExportJSON}>
                         <Download size={14} style={{marginRight: 6}}/> Save JSON
                      </button>
                   </div>
                   
                   <div style={{...styles.csvBox, background: theme.bg, borderColor: theme.border}}>
                      <p style={{fontSize: 12, marginBottom: 8, color: theme.text}}>Batch Generation (CSV)</p>
                      <label style={{...styles.csvBtn, borderColor: theme.border, color: theme.primary, background: theme.surface}}>
                         <Sparkles size={14} style={{marginRight: 6}}/> Upload CSV & Zip
                         <input type="file" hidden onChange={handleBulkCSV} accept=".csv" />
                      </label>
                   </div>
                </div>

                <div style={styles.sidebarSection}>
                   <h5 style={{...styles.sectionTitle, color: theme.textSecondary}}>2. Field Mapping (Page {currentPage + 1})</h5>
                   <div style={styles.fieldList}>
                      {currentBoxes.map(box => (
                          <div key={box.id} 
                               onClick={() => setSelectedBoxId(box.id)}
                               style={{
                                 ...styles.fieldItem, 
                                 background: theme.bg,
                                 borderColor: theme.border,
                                 borderLeft: selectedBoxId === box.id ? `4px solid ${theme.primary}` : `4px solid transparent`
                               }}>
                               <div style={styles.fieldRow}>
                                  <span style={{...styles.fieldLabel, color: theme.textSecondary}}>Field Name</span>
                                  <input 
                                    value={box.fieldName} 
                                    onChange={(e) => updateBoxValue(box.id, 'fieldName', e.target.value)}
                                    style={{...styles.fieldInput, background: theme.surface, color: theme.text, borderColor: theme.border}} 
                                  />
                               </div>
                               <div style={styles.fieldRow}>
                                  <span style={{...styles.fieldLabel, color: theme.textSecondary}}>Type</span>
                                  <select value={box.type} onChange={(e) => updateBoxValue(box.id, 'type', e.target.value)} style={{...styles.fieldSelect, background: theme.surface, color: theme.text, borderColor: theme.border}}>
                                       <option value="text">Text</option>
                                       <option value="checkbox">Checkbox</option>
                                       <option value="signature">Signature</option>
                                  </select>
                               </div>
                          </div>
                      ))}
                   </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* WORKSPACE AREA */}
        <div ref={containerRef} style={{
            ...styles.workspace, 
            background: theme.bg, 
            backgroundImage: `radial-gradient(${theme.grid} 1px, transparent 1px)`
        }}>
            
            {/* Empty State */}
            {pages.length === 0 && (
                <div style={styles.emptyState}>
                    <motion.div 
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        style={{...styles.uploadCard, background: theme.surface, color: theme.text}}
                    >
                        <div style={{...styles.uploadIconCircle, background: isDarkMode ? "#334155" : "#EFF6FF"}}>
                          <Upload size={32} color={theme.primary}/>
                        </div>
                        <h2>Upload Document</h2>
                        <p style={{color: theme.textSecondary}}>Drag & drop or click to upload your PDF</p>
                        <button onClick={() => fileInputRef.current.click()} style={{...styles.largeUploadBtn, background: theme.primary}}>
                           Select PDF File
                        </button>
                        <input ref={fileInputRef} type="file" hidden onChange={handleUpload} accept="application/pdf" />
                    </motion.div>
                </div>
            )}

            {/* SCROLLABLE CANVAS CONTAINER */}
            {pages.length > 0 && (
                <div style={styles.canvasContainer}>
                    <div style={{
                        ...styles.canvasWrapper,
                        boxShadow: isDarkMode ? "0 20px 25px -5px rgba(0, 0, 0, 0.5)" : "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                        position: 'relative' // Needed for Overlay Input
                    }}>
                        <Stage 
                          width={originalWidth * scale} 
                          height={originalHeight * scale} 
                          scale={{ x: scale, y: scale }} 
                          ref={stageRef} 
                          onMouseDown={e => { if(e.target === e.target.getStage()) setSelectedBoxId(null); }}
                        >
                            <Layer>
                                <URLImage src={imageData} width={originalWidth} height={originalHeight} />
                                {currentBoxes.map(box => {
                                    const isSelected = selectedBoxId === box.id;
                                    return (
                                        <Group
                                            key={box.id}
                                            x={box.x} y={box.y}
                                            draggable={!isSaving}
                                            onDragEnd={(e) => updateBoxValue(box.id, 'x', e.target.x())}
                                            onClick={() => handleBoxClick(box)}
                                            onMouseEnter={() => document.body.style.cursor = "move"}
                                            onMouseLeave={() => document.body.style.cursor = "default"}
                                        >
                                            <Rect 
                                                width={box.width} height={box.height} 
                                                stroke={isSelected ? theme.primary : (box.type === "checkbox" ? theme.danger : (box.type === "signature" ? "#9333EA" : theme.success))}
                                                strokeWidth={isSelected ? 2 / scale : 1 / scale}
                                                fill={isSelected ? (isDarkMode ? "rgba(59, 130, 246, 0.2)" : "rgba(37, 99, 235, 0.1)") : "transparent"}
                                                cornerRadius={2}
                                            />
                                            {/* Only show Text if NOT currently editing (handled by Overlay Input) */}
                                            {box.type === "text" && selectedBoxId !== box.id && (
                                                <Text text={box.userText} width={box.width} height={box.height} fontSize={fontSize} align="center" verticalAlign="middle" fontFamily="sans-serif" />
                                            )}
                                            {box.type === "checkbox" && box.userText === "X" && (
                                                <Group>
                                                  <Line points={[0,0, box.width, box.height]} stroke="black" strokeWidth={3} />
                                                  <Line points={[box.width,0, 0, box.height]} stroke="black" strokeWidth={3} />
                                                </Group>
                                            )}
                                            {box.type === "signature" && box.signatureUrl ? (
                                                 <URLImage src={box.signatureUrl} x={0} y={0} width={box.width} height={box.height} />
                                            ) : (
                                                box.type === "signature" && <Text text="Sign Here" width={box.width} height={box.height} align="center" verticalAlign="middle" opacity={0.5} />
                                            )}
                                            {mode === 'simple' && isSelected && (
                                                <Circle x={box.width} y={0} radius={8 / scale} fill={theme.danger} onClick={(e) => handleDelete(e, box.id)} />
                                            )}
                                        </Group>
                                    );
                                })}
                            </Layer>
                        </Stage>
                        
                        {/* 🚀 DIRECT TYPING OVERLAY */}
                        {mode === 'simple' && selectedBoxId && currentBoxes.find(b => b.id === selectedBoxId)?.type === "text" && (() => {
                             const box = currentBoxes.find(b => b.id === selectedBoxId);
                             return (
                                 <textarea
                                    autoFocus
                                    value={box.userText}
                                    onChange={(e) => updateBoxValue(box.id, 'userText', e.target.value)}
                                    style={{
                                        position: 'absolute',
                                        top: box.y * scale,
                                        left: box.x * scale,
                                        width: box.width * scale,
                                        height: box.height * scale,
                                        fontSize: `${fontSize * scale}px`,
                                        fontFamily: 'sans-serif',
                                        textAlign: 'center',
                                        border: `2px solid ${theme.primary}`,
                                        background: 'rgba(255,255,255,0.9)',
                                        color: 'black',
                                        padding: 0,
                                        margin: 0,
                                        outline: 'none',
                                        resize: 'none',
                                        overflow: 'hidden',
                                        lineHeight: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 100
                                    }}
                                 />
                             );
                        })()}

                    </div>
                </div>
            )}
            
            {/* FLOATING CONTROLS (Updated with Font Controls) */}
            {pages.length > 0 && (
                <div style={{...styles.floatingControls, background: theme.surface, color: theme.text}}>
                    <div style={styles.controlGroup}>
                        <button onClick={handleUndo} disabled={historyStep <= 0} style={{...styles.iconBtn, color: theme.text}} title="Undo">
                            <Undo size={18} />
                        </button>
                        <button onClick={handleRedo} disabled={historyStep >= history.length - 1} style={{...styles.iconBtn, color: theme.text}} title="Redo">
                            <Redo size={18} />
                        </button>
                    </div>
                    <div style={{...styles.controlDivider, background: theme.border}} />
                    
                    {/* FONT CONTROLS */}
                    <div style={styles.controlGroup}>
                        <button onClick={() => setFontSize(s => Math.max(8, s - 2))} style={{...styles.iconBtn, color: theme.text}} title="Decrease Font">
                           <Minus size={14} />
                        </button>
                        <span style={{fontSize: 12, width: 25, textAlign: 'center', fontWeight: 'bold'}}>{fontSize}</span>
                        <button onClick={() => setFontSize(s => Math.min(40, s + 2))} style={{...styles.iconBtn, color: theme.text}} title="Increase Font">
                           <Plus size={14} />
                        </button>
                    </div>

                    <div style={{...styles.controlDivider, background: theme.border}} />
                    <div style={styles.controlGroup}>
                        <button onClick={() => setScale(s => Math.max(0.3, s - 0.1))} style={{...styles.iconBtn, color: theme.text}}><ZoomOut size={18}/></button>
                        <span style={{fontSize: 12, width: 40, textAlign: 'center'}}>{Math.round(scale * 100)}%</span>
                        <button onClick={() => setScale(s => Math.min(3.0, s + 0.1))} style={{...styles.iconBtn, color: theme.text}}><ZoomIn size={18}/></button>
                    </div>
                    <div style={{...styles.controlDivider, background: theme.border}} />
                    <div style={styles.controlGroup}>
                         <button disabled={currentPage === 0} onClick={() => setCurrentPage(c => c - 1)} style={{...styles.iconBtn, color: theme.text}}><ChevronLeft size={18}/></button>
                         <span style={{fontSize: 12, width: 60, textAlign: 'center'}}>Page {currentPage + 1}/{pages.length}</span>
                         <button disabled={currentPage === pages.length - 1} onClick={() => setCurrentPage(c => c + 1)} style={{...styles.iconBtn, color: theme.text}}><ChevronRight size={18}/></button>
                    </div>
                </div>
            )}
        </div>
      </div>

      {/* --- OVERLAYS --- */}
      
      {/* 🌟 MAGIC PASTE MODAL 🌟 */}
      <AnimatePresence>
        {showMagicPaste && (
            <div style={styles.modalOverlay}>
                <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    style={{...styles.modalContent, background: theme.surface, color: theme.text}}
                >
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 15}}>
                        <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}><Bot size={24} color="#7C3AED"/> AI Magic Paste</h3>
                        <button onClick={() => setShowMagicPaste(false)} style={{...styles.iconBtn, color: theme.text}}><X size={20}/></button>
                    </div>
                    <p style={{color: theme.textSecondary, marginBottom: "12px", fontSize: "14px"}}>
                        Paste your Resume, Cover Letter, or LinkedIn Bio below. Gemini AI will intelligently read the text and fill the form fields for you.
                    </p>
                    <textarea 
                        autoFocus
                        value={pasteText}
                        onChange={(e) => setPasteText(e.target.value)}
                        placeholder="Example: My name is Alice, I was born on 1990-01-01, and I live at..."
                        style={{
                            width: "100%", 
                            height: "200px", 
                            padding: "16px", 
                            borderRadius: "12px", 
                            border: `2px solid ${theme.border}`, 
                            background: theme.bg, 
                            color: theme.text,
                            fontSize: "14px",
                            outline: "none",
                            resize: "none"
                        }}
                    />
                    <div style={styles.modalActions}>
                        <button onClick={() => setShowMagicPaste(false)} style={{...styles.secondaryBtn, borderColor: theme.border, color: theme.text}}>Cancel</button>
                        <button onClick={handleMagicFill} style={{...styles.primaryBtn, background: "linear-gradient(135deg, #6366F1 0%, #A855F7 100%)"}}>
                           <Sparkles size={16} style={{marginRight: 6}}/> Auto-Fill Form
                        </button>
                    </div>
                </motion.div>
            </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSigModal && (
            <div style={styles.modalOverlay}>
                <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    style={{...styles.modalContent, background: theme.surface, color: theme.text}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 15}}>
                        <h3>Sign Document</h3>
                        <button onClick={() => setShowSigModal(false)} style={{...styles.iconBtn, color: theme.text}}><X size={20}/></button>
                    </div>
                    <div style={{...styles.sigCanvasWrapper, borderColor: theme.border, background: isDarkMode ? "#fff" : "#F9FAFB"}}> 
                        <SignatureCanvas ref={sigPadRef} canvasProps={{width: 500, height: 200, className: 'sigCanvas'}} />
                    </div>
                    <div style={styles.modalActions}>
                        <button onClick={() => sigPadRef.current.clear()} style={{...styles.secondaryBtn, borderColor: theme.border, color: theme.text}}>Clear</button>
                        <button onClick={saveSignature} style={{...styles.primaryBtn, background: theme.primary}}>Save Signature</button>
                    </div>
                </motion.div>
            </div>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
         {loading && <LoadingOverlay />}
      </AnimatePresence>
    </div>
  );
}

// --- STYLES ---
const styles = {
  appContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    fontFamily: "'Inter', sans-serif",
    transition: "background 0.3s, color 0.3s"
  },
  header: {
    height: "64px",
    borderBottom: `1px solid`,
    display: "flex",
    alignItems: "center",
    padding: "0 24px",
    justifyContent: "space-between",
    zIndex: 20,
    transition: "background 0.3s, border-color 0.3s"
  },
  logoSection: {
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },
  logoIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  logoText: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "700",
    letterSpacing: "-0.5px"
  },
  modeSwitch: {
    padding: "4px",
    borderRadius: "8px",
    display: "flex",
    gap: "4px"
  },
  modeBtn: {
    display: "flex",
    alignItems: "center",
    padding: "6px 12px",
    border: "none",
    background: "transparent",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    borderRadius: "6px",
    transition: "all 0.2s"
  },
  modeBtnActive: {
    display: "flex",
    alignItems: "center",
    padding: "6px 12px",
    border: "none",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    borderRadius: "6px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
    transition: "all 0.2s"
  },
  mainLayout: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  sidebar: {
    width: "320px",
    borderRight: `1px solid`,
    display: "flex",
    flexDirection: "column",
    zIndex: 10,
    transition: "background 0.3s, border-color 0.3s"
  },
  sidebarHeader: {
    padding: "20px",
    borderBottom: `1px solid`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  sidebarContent: {
    flex: 1,
    overflowY: "auto",
    padding: "20px"
  },
  sidebarSection: {
    marginBottom: "24px"
  },
  sectionTitle: {
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "12px"
  },
  buttonGroup: {
    display: "flex",
    gap: "10px"
  },
  csvBox: {
    marginTop: "12px",
    padding: "12px",
    border: `1px dashed`,
    borderRadius: "6px"
  },
  csvBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: "8px",
    border: `1px solid`,
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer"
  },
  fieldList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  fieldItem: {
    padding: "10px",
    borderRadius: "6px",
    border: `1px solid`,
    cursor: "pointer",
    transition: "all 0.2s"
  },
  fieldRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "6px"
  },
  fieldLabel: {
    fontSize: "11px",
    fontWeight: "600"
  },
  fieldInput: {
    border: `1px solid`,
    borderRadius: "4px",
    padding: "4px",
    fontSize: "12px",
    width: "120px"
  },
  fieldSelect: {
    border: `1px solid`,
    borderRadius: "4px",
    padding: "4px",
    fontSize: "12px",
    width: "120px"
  },
  workspace: {
    flex: 1,
    backgroundSize: "20px 20px",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
    transition: "background 0.3s"
  },
  emptyState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%"
  },
  uploadCard: {
    padding: "40px",
    borderRadius: "16px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
    textAlign: "center",
    width: "400px"
  },
  uploadIconCircle: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px auto"
  },
  largeUploadBtn: {
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    marginTop: "20px",
    cursor: "pointer",
    boxShadow: "0 4px 6px rgba(0,0,0,0.2)"
  },
  canvasContainer: {
    flex: 1,
    overflow: "auto",
    padding: "40px",
    textAlign: "center" 
  },
  canvasWrapper: {
    display: "inline-block", 
    borderRadius: "4px",
    overflow: "hidden",
    textAlign: "left"
  },
  floatingControls: {
    position: "absolute",
    bottom: "30px",
    left: "50%",
    transform: "translateX(-50%)",
    padding: "8px 16px",
    borderRadius: "12px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    zIndex: 50
  },
  controlGroup: {
    display: "flex",
    alignItems: "center",
    gap: "4px"
  },
  controlDivider: {
    width: "1px",
    height: "20px"
  },
  iconBtn: {
    background: "transparent",
    border: "none",
    padding: "8px",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.2s"
  },
  primaryBtn: {
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    cursor: "pointer"
  },
  secondaryBtn: {
    border: `1px solid`,
    padding: "8px 12px",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flex: 1
  },
  floatingEditor: {
    display: 'none' // Hidden by default now
  },
  modalOverlay: {
    position: "fixed",
    top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.4)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  },
  modalContent: {
    padding: "24px",
    borderRadius: "16px",
    width: "550px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
  },
  sigCanvasWrapper: {
    border: `1px dashed`,
    borderRadius: "8px",
    overflow: "hidden"
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "20px"
  },
  loadingOverlay: {
    position: "fixed",
    top: 0, left: 0, right: 0, bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000
  },
  loadingSpinner: {
    width: "40px",
    height: "40px",
    border: `4px solid`,
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  }
};

const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  body { margin: 0; padding: 0; box-sizing: border-box; }
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 8px; height: 8px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 4px; }
  ::-webkit-scrollbar-thumb:hover { background: #94A3B8; }
`;
document.head.appendChild(styleSheet);

export default App;